import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useOrganization } from "../organization/OrganizationProvider";
import "./PurchasesPage.css";

type SupplierRow = {
  id: string;
  supplier_type: "person" | "company";
  document_type: string | null;
  document_number: string | null;
  name: string;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  whatsapp: string | null;
  city: string | null;
  country_code: string;
  status: "active" | "inactive" | "archived";
};

const SUPPLIER_QUOTE_STATUS_LABELS: Record<string, string> = {
  draft: "Borrador",
  sent: "Enviada",
  received: "Recibida",
  cancelled: "Cancelada",
};

const PURCHASE_STATUS_LABELS: Record<string, string> = {
  draft: "Borrador",
  ordered: "Emitida",
  partially_received: "Parcialmente recibida",
  received: "Recibida",
  cancelled: "Cancelada",
};

type PurchaseRow = {
  id: string;
  purchase_number: string;
  status: "draft" | "ordered" | "partially_received" | "received" | "cancelled";
  order_date: string;
  expected_date: string | null;
  currency_code: string;
  total: number | string;
  purchase_scope: string;
  supplier_id: string;
  supplier_name: string;
  warehouse_id: string | null;
  warehouse_name: string | null;
  updated_at: string;
};

type SupplierQuoteRequest = {
  id: string;
  status: string;
  supplier_id: string;
  supplier_name: string;
  supplier_email: string | null;
  supplier_whatsapp: string | null;
  sku: string | null;
  item_name: string;
  warehouse_code: string | null;
  requested_quantity: number | string;
  requested_at: string;
  sent_at: string | null;
  received_at: string | null;
};

type WarehouseRow = {
  id: string;
  name: string;
  status?: string;
};

type CatalogRow = {
  id: string;
  item_type: string;
  sku: string | null;
  name: string;
  cost_price: number | string;
  tax_rate: number | string;
  status: string;
};

type Line = {
  catalogItemId: string;
  description: string;
  quantity: string;
  unitCost: string;
  discountAmount: string;
  taxRate: string;
};

const EMPTY_LINE: Line = {
  catalogItemId: "",
  description: "",
  quantity: "1",
  unitCost: "0",
  discountAmount: "0",
  taxRate: "18",
};

function money(value: number | string, currency = "PEN") {
  return new Intl.NumberFormat("es-PE", {
    style: "currency",
    currency,
  }).format(Number(value || 0));
}

function today() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Lima",
  }).format(new Date());
}

export function PurchasesPage() {
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.organizationId ?? null;

  const [suppliers, setSuppliers] = useState<SupplierRow[]>([]);
  const [purchases, setPurchases] = useState<PurchaseRow[]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseRow[]>([]);
  const [catalog, setCatalog] = useState<CatalogRow[]>([]);
  const [quoteRequests, setQuoteRequests] = useState<SupplierQuoteRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const [supplierOpen, setSupplierOpen] = useState(false);
  const [supplierName, setSupplierName] = useState("");
  const [supplierDocument, setSupplierDocument] = useState("");
  const [supplierEmail, setSupplierEmail] = useState("");

  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [supplierId, setSupplierId] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [orderDate, setOrderDate] = useState(today());
  const [expectedDate, setExpectedDate] = useState("");
  const [currency, setCurrency] = useState("PEN");
  const [scope, setScope] = useState("domestic");
  const [lines, setLines] = useState<Line[]>([{ ...EMPTY_LINE }]);

  const [busy, setBusy] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [quoteBusyId, setQuoteBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!organizationId) return;

    setLoading(true);
    setError(null);

    const [
      supplierResult,
      purchaseResult,
      warehouseResult,
      catalogResult,
      quoteRequestResult,
    ] = await Promise.all([
      supabase.rpc("list_organization_suppliers", {
        p_organization_id: organizationId,
        p_search: null,
      }),
      supabase.rpc("list_organization_purchase_orders", {
        p_organization_id: organizationId,
        p_search: search.trim() || null,
      }),
      supabase.rpc("list_inventory_warehouses", {
        p_organization_id: organizationId,
      }),
      supabase.rpc("list_organization_catalog", {
        p_organization_id: organizationId,
        p_search: null,
        p_item_type: "product",
      }),
      supabase.rpc("list_supplier_quote_requests", {
        p_organization_id: organizationId,
      }),
    ]);

    const firstError =
      supplierResult.error ??
      purchaseResult.error ??
      warehouseResult.error ??
      catalogResult.error ??
      quoteRequestResult.error;

    if (firstError) setError(firstError.message);

    setSuppliers(
      supplierResult.error
        ? []
        : ((supplierResult.data ?? []) as SupplierRow[]).filter(
            (supplier) => supplier.status === "active",
          ),
    );
    setPurchases(
      purchaseResult.error ? [] : ((purchaseResult.data ?? []) as PurchaseRow[]),
    );
    setWarehouses(
      warehouseResult.error
        ? []
        : ((warehouseResult.data ?? []) as WarehouseRow[]).filter(
            (warehouse) => !warehouse.status || warehouse.status === "active",
          ),
    );
    setCatalog(
      catalogResult.error
        ? []
        : ((catalogResult.data ?? []) as CatalogRow[]).filter(
            (item) => item.status === "active",
          ),
    );
    setQuoteRequests(
      quoteRequestResult.error
        ? []
        : ((quoteRequestResult.data ?? []) as SupplierQuoteRequest[]),
    );
    setLoading(false);
  }, [organizationId, search]);

  useEffect(() => {
    const timer = window.setTimeout(() => void loadData(), 220);
    return () => window.clearTimeout(timer);
  }, [loadData]);

  const draftCount = useMemo(
    () => purchases.filter((purchase) => purchase.status === "draft").length,
    [purchases],
  );

  const pendingCount = useMemo(
    () =>
      purchases.filter((purchase) =>
        ["ordered", "partially_received"].includes(purchase.status),
      ).length,
    [purchases],
  );

  async function createSupplier(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!organizationId) return;

    setBusy(true);
    setError(null);
    setSuccess(null);

    const { error: rpcError } = await supabase.rpc(
      "create_organization_supplier",
      {
        p_organization_id: organizationId,
        p_supplier_type: "company",
        p_name: supplierName,
        p_document_type: supplierDocument.trim() ? "RUC" : null,
        p_document_number: supplierDocument.trim() || null,
        p_contact_name: null,
        p_email: supplierEmail.trim() || null,
        p_phone: null,
        p_whatsapp: null,
        p_address: null,
        p_city: null,
        p_country_code: "PE",
        p_notes: null,
      },
    );

    setBusy(false);

    if (rpcError) {
      setError(rpcError.message);
      return;
    }

    setSupplierName("");
    setSupplierDocument("");
    setSupplierEmail("");
    setSupplierOpen(false);
    setSuccess("Proveedor creado.");
    await loadData();
  }

  function updateLine(index: number, patch: Partial<Line>) {
    setLines((current) =>
      current.map((line, currentIndex) =>
        currentIndex === index ? { ...line, ...patch } : line,
      ),
    );
  }

  function chooseCatalog(index: number, catalogItemId: string) {
    const item = catalog.find((candidate) => candidate.id === catalogItemId);

    updateLine(index, {
      catalogItemId,
      description: item?.name ?? "",
      unitCost: String(item?.cost_price ?? 0),
      taxRate: String(item?.tax_rate ?? 18),
    });
  }

  async function transitionQuoteRequest(
    request: SupplierQuoteRequest,
    status: "sent" | "received" | "cancelled",
  ) {
    setQuoteBusyId(request.id);
    setError(null);
    setSuccess(null);

    const { error: rpcError } = await supabase.rpc(
      "transition_supplier_quote_request",
      {
        p_request_id: request.id,
        p_status: status,
      },
    );

    setQuoteBusyId(null);

    if (rpcError) {
      setError(rpcError.message);
      return false;
    }

    setSuccess(
      status === "sent"
        ? "Solicitud marcada como enviada."
        : status === "received"
          ? "Respuesta del proveedor registrada."
          : "Solicitud cancelada.",
    );
    await loadData();
    return true;
  }

  async function contactSupplier(
    request: SupplierQuoteRequest,
    channel: "whatsapp" | "email",
  ) {
    const ok = await transitionQuoteRequest(request, "sent");
    if (!ok) return;

    const message = [
      "Solicitud de cotización",
      `Producto: ${request.item_name}`,
      `SKU: ${request.sku ?? "N/A"}`,
      `Cantidad requerida: ${Number(request.requested_quantity)}`,
      request.warehouse_code ? `Almacén: ${request.warehouse_code}` : "",
    ]
      .filter(Boolean)
      .join("\n");

    if (channel === "whatsapp" && request.supplier_whatsapp) {
      const phone = request.supplier_whatsapp.replace(/\D/g, "");
      window.open(
        `https://wa.me/${phone}?text=${encodeURIComponent(message)}`,
        "_blank",
        "noopener,noreferrer",
      );
      return;
    }

    if (channel === "email" && request.supplier_email) {
      window.location.href =
        `mailto:${request.supplier_email}?subject=${encodeURIComponent(
          "Solicitud de cotización " + (request.sku ?? request.item_name),
        )}&body=${encodeURIComponent(message)}`;
    }
  }

  async function createPurchase(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!organizationId) return;

    if (!supplierId) {
      setError("Selecciona un proveedor.");
      return;
    }

    const payload = lines.map((line) => ({
      catalog_item_id: line.catalogItemId || null,
      description: line.description.trim(),
      quantity: Number(line.quantity),
      unit_cost: Number(line.unitCost),
      discount_amount: Number(line.discountAmount),
      tax_rate: Number(line.taxRate),
    }));

    if (payload.some((line) => !line.description || line.quantity <= 0)) {
      setError("Revisa la descripción y cantidad de cada línea.");
      return;
    }

    setBusy(true);
    setError(null);
    setSuccess(null);

    const { error: rpcError } = await supabase.rpc(
      "create_organization_purchase_order",
      {
        p_organization_id: organizationId,
        p_supplier_id: supplierId,
        p_warehouse_id: warehouseId || null,
        p_order_date: orderDate,
        p_expected_date: expectedDate || null,
        p_currency_code: currency,
        p_purchase_scope: scope,
        p_notes: null,
        p_items: payload,
      },
    );

    setBusy(false);

    if (rpcError) {
      setError(rpcError.message);
      return;
    }

    setPurchaseOpen(false);
    setSupplierId("");
    setWarehouseId("");
    setExpectedDate("");
    setLines([{ ...EMPTY_LINE }]);
    setSuccess("Orden de compra creada.");
    await loadData();
  }

  async function transitionPurchase(id: string, status: "ordered" | "cancelled") {
    setBusyId(id);
    setError(null);
    setSuccess(null);

    const { error: rpcError } = await supabase.rpc(
      "transition_purchase_order_status",
      {
        p_purchase_order_id: id,
        p_new_status: status,
      },
    );

    setBusyId(null);

    if (rpcError) {
      setError(rpcError.message);
      return;
    }

    setSuccess(status === "ordered" ? "Orden emitida." : "Orden cancelada.");
    await loadData();
  }

  if (!selectedOrganization) {
    return (
      <div className="page">
        <section className="empty-state">
          <strong>Selecciona una empresa.</strong>
          <p>Necesitas una empresa activa para trabajar con compras.</p>
        </section>
      </div>
    );
  }

  return (
    <div className="page">
      <header className="page-header page-header-actions">
        <div>
          <span className="eyebrow">Compras</span>
          <h1>{selectedOrganization.name}</h1>
          <p>Proveedores y órdenes de compra integradas al inventario.</p>
        </div>
        <div className="purchase-header-actions">
          <button
            className="ghost-button"
            type="button"
            onClick={() => setSupplierOpen((value) => !value)}
          >
            Nuevo proveedor
          </button>
          <button
            className="primary-button"
            type="button"
            onClick={() => setPurchaseOpen((value) => !value)}
          >
            Nueva compra
          </button>
        </div>
      </header>

      <section className="purchase-summary-grid">
        <div className="metric-card">
          <span>Proveedores activos</span>
          <strong>{loading ? "…" : suppliers.length}</strong>
        </div>
        <div className="metric-card">
          <span>Borradores</span>
          <strong>{loading ? "…" : draftCount}</strong>
        </div>
        <div className="metric-card">
          <span>Pendientes de recepción</span>
          <strong>{loading ? "…" : pendingCount}</strong>
        </div>
      </section>

      <section className="panel-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Reposición</span>
            <h2>Solicitudes de cotización a proveedores</h2>
          </div>
        </div>

        {quoteRequests.length === 0 ? (
          <p className="settings-empty">
            No hay solicitudes generadas desde Inventario.
          </p>
        ) : (
          <div className="supplier-quote-grid">
            {quoteRequests.map((request) => (
              <article className="supplier-quote-card" key={request.id}>
                <span className="status-badge">
                  {SUPPLIER_QUOTE_STATUS_LABELS[request.status] ?? request.status}
                </span>
                <strong>{request.item_name}</strong>
                <span>{request.sku ?? "Sin SKU"}</span>
                <dl>
                  <div>
                    <dt>Proveedor</dt>
                    <dd>{request.supplier_name}</dd>
                  </div>
                  <div>
                    <dt>Almacén</dt>
                    <dd>{request.warehouse_code ?? "—"}</dd>
                  </div>
                  <div>
                    <dt>Cantidad</dt>
                    <dd>{Number(request.requested_quantity)}</dd>
                  </div>
                </dl>
                <small>
                  {new Date(request.requested_at).toLocaleString("es-PE")}
                </small>

                <div className="supplier-quote-actions">
                  {request.status === "draft" && request.supplier_whatsapp ? (
                    <button
                      className="compact-button"
                      type="button"
                      disabled={quoteBusyId === request.id}
                      onClick={() => void contactSupplier(request, "whatsapp")}
                    >
                      WhatsApp
                    </button>
                  ) : null}
                  {request.status === "draft" && request.supplier_email ? (
                    <button
                      className="compact-button"
                      type="button"
                      disabled={quoteBusyId === request.id}
                      onClick={() => void contactSupplier(request, "email")}
                    >
                      Correo
                    </button>
                  ) : null}
                  {request.status === "sent" ? (
                    <button
                      className="compact-button"
                      type="button"
                      disabled={quoteBusyId === request.id}
                      onClick={() => void transitionQuoteRequest(request, "received")}
                    >
                      Marcar recibida
                    </button>
                  ) : null}
                  {!["received", "cancelled"].includes(request.status) ? (
                    <button
                      className="compact-button compact-button-danger"
                      type="button"
                      disabled={quoteBusyId === request.id}
                      onClick={() => void transitionQuoteRequest(request, "cancelled")}
                    >
                      Cancelar
                    </button>
                  ) : null}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {supplierOpen ? (
        <section className="panel-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Proveedor</span>
              <h2>Nuevo proveedor</h2>
            </div>
          </div>
          <form className="purchase-simple-form" onSubmit={createSupplier}>
            <label>
              Razón social
              <input
                required
                value={supplierName}
                onChange={(event) => setSupplierName(event.target.value)}
              />
            </label>
            <label>
              RUC
              <input
                value={supplierDocument}
                onChange={(event) => setSupplierDocument(event.target.value)}
              />
            </label>
            <label>
              Correo
              <input
                type="email"
                value={supplierEmail}
                onChange={(event) => setSupplierEmail(event.target.value)}
              />
            </label>
            <div className="form-actions field-span-full">
              <button className="primary-button" type="submit" disabled={busy}>
                {busy ? "Guardando…" : "Guardar proveedor"}
              </button>
            </div>
          </form>
        </section>
      ) : null}

      {purchaseOpen ? (
        <section className="panel-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Orden</span>
              <h2>Nueva orden de compra</h2>
            </div>
          </div>

          <form className="purchase-form" onSubmit={createPurchase}>
            <div className="purchase-header-grid">
              <label>
                Proveedor
                <select
                  required
                  value={supplierId}
                  onChange={(event) => setSupplierId(event.target.value)}
                >
                  <option value="">Seleccionar</option>
                  {suppliers.map((supplier) => (
                    <option key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Almacén destino
                <select
                  value={warehouseId}
                  onChange={(event) => setWarehouseId(event.target.value)}
                >
                  <option value="">Sin definir</option>
                  {warehouses.map((warehouse) => (
                    <option key={warehouse.id} value={warehouse.id}>
                      {warehouse.name}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Fecha
                <input
                  type="date"
                  value={orderDate}
                  onChange={(event) => setOrderDate(event.target.value)}
                />
              </label>

              <label>
                Fecha esperada
                <input
                  type="date"
                  value={expectedDate}
                  onChange={(event) => setExpectedDate(event.target.value)}
                />
              </label>

              <label>
                Moneda
                <select value={currency} onChange={(event) => setCurrency(event.target.value)}>
                  <option value="PEN">PEN</option>
                  <option value="USD">USD</option>
                </select>
              </label>

              <label>
                Alcance
                <select value={scope} onChange={(event) => setScope(event.target.value)}>
                  <option value="domestic">Nacional</option>
                  <option value="foreign">Exterior</option>
                  <option value="import">Importación</option>
                </select>
              </label>
            </div>

            <div className="purchase-lines">
              {lines.map((line, index) => (
                <div className="purchase-line" key={index}>
                  <label>
                    Catálogo
                    <select
                      value={line.catalogItemId}
                      onChange={(event) => chooseCatalog(index, event.target.value)}
                    >
                      <option value="">Manual</option>
                      {catalog.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.sku ? `${item.sku} · ` : ""}
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="purchase-line-description">
                    Descripción
                    <input
                      required
                      value={line.description}
                      onChange={(event) =>
                        updateLine(index, { description: event.target.value })
                      }
                    />
                  </label>
                  <label>
                    Cantidad
                    <input
                      type="number"
                      min="0.001"
                      step="0.001"
                      value={line.quantity}
                      onChange={(event) =>
                        updateLine(index, { quantity: event.target.value })
                      }
                    />
                  </label>
                  <label>
                    Costo
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={line.unitCost}
                      onChange={(event) =>
                        updateLine(index, { unitCost: event.target.value })
                      }
                    />
                  </label>
                  <label>
                    IGV %
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={line.taxRate}
                      onChange={(event) =>
                        updateLine(index, { taxRate: event.target.value })
                      }
                    />
                  </label>
                  <button
                    className="danger-ghost-button"
                    type="button"
                    disabled={lines.length === 1}
                    onClick={() =>
                      setLines((current) =>
                        current.filter((_, currentIndex) => currentIndex !== index),
                      )
                    }
                  >
                    Quitar
                  </button>
                </div>
              ))}
            </div>

            <div className="purchase-form-actions">
              <button
                className="ghost-button"
                type="button"
                onClick={() =>
                  setLines((current) => [...current, { ...EMPTY_LINE }])
                }
              >
                + Línea
              </button>
              <button className="primary-button" type="submit" disabled={busy}>
                {busy ? "Guardando…" : "Guardar compra"}
              </button>
            </div>
          </form>
        </section>
      ) : null}

      {error ? <div className="inline-alert">{error}</div> : null}
      {success ? <div className="form-success purchase-message">{success}</div> : null}

      <section className="toolbar-card">
        <div>
          <strong>{loading ? "…" : purchases.length}</strong>
          <span> órdenes</span>
        </div>
        <input
          type="search"
          placeholder="Buscar por orden o proveedor"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </section>

      <section className="purchase-grid">
        {purchases.map((purchase) => (
          <article className="purchase-card" key={purchase.id}>
            <div className="purchase-card-heading">
              <div>
                <span className={`status-badge purchase-status-${PURCHASE_STATUS_LABELS[purchase.status] ?? purchase.status}`}>
                  {purchase.status}
                </span>
                <h2>{purchase.purchase_number}</h2>
                <p>{purchase.supplier_name}</p>
              </div>
              <strong>{money(purchase.total, purchase.currency_code)}</strong>
            </div>

            <dl className="customer-meta">
              <div>
                <dt>Fecha</dt>
                <dd>{new Date(purchase.order_date + "T00:00:00").toLocaleDateString("es-PE")}</dd>
              </div>
              <div>
                <dt>Entrega esperada</dt>
                <dd>
                  {purchase.expected_date
                    ? new Date(purchase.expected_date + "T00:00:00").toLocaleDateString("es-PE")
                    : "—"}
                </dd>
              </div>
              <div>
                <dt>Almacén</dt>
                <dd>{purchase.warehouse_name ?? "—"}</dd>
              </div>
              <div>
                <dt>Tipo</dt>
                <dd>{purchase.purchase_scope}</dd>
              </div>
            </dl>

            <div className="quotation-actions">
              <Link className="compact-button" to={`/compras/${purchase.id}`}>
                Ver detalle
              </Link>
              {purchase.status === "draft" ? (
                <>
                  <button
                    className="compact-button"
                    type="button"
                    disabled={busyId === purchase.id}
                    onClick={() => void transitionPurchase(purchase.id, "ordered")}
                  >
                    Emitir orden
                  </button>
                  <button
                    className="compact-button compact-button-danger"
                    type="button"
                    disabled={busyId === purchase.id}
                    onClick={() => void transitionPurchase(purchase.id, "cancelled")}
                  >
                    Cancelar
                  </button>
                </>
              ) : null}
            </div>
          </article>
        ))}
      </section>
    </div>
  );
}
